import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Plus, CalendarDays, Users, Settings2, ExternalLink, Hourglass, FileText, Upload, Check, X, Download } from "lucide-react";

const CAPACITIES = [4, 10, 36, 38, 72, 120, 409];
const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(b / 1024)} kB`);

export default function RoomManage() {
  const { id } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [tab, setTab] = useState("settings");
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [waiting, setWaiting] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const [newMember, setNewMember] = useState({ email: "", role: "participant" });
  const [newEvent, setNewEvent] = useState({ title: "", description: "", start_time: "", duration_minutes: 60, recurrence: "none" });

  const ROLE_LABEL = { organizer: t("manage.role_organizer"), moderator: t("manage.role_moderator"), participant: t("manage.role_participant") };
  const REC_LABEL = { none: t("manage.rec_none"), daily: t("manage.rec_daily"), weekly: t("manage.rec_weekly"), monthly: t("manage.rec_monthly") };

  const load = useCallback(() => {
    api.get(`/rooms/${id}`).then(({ data }) => setRoom(data)).catch(() => navigate("/dashboard"));
    api.get(`/rooms/${id}/members`).then(({ data }) => setMembers(data)).catch(() => {});
    api.get(`/rooms/${id}/events`).then(({ data }) => setEvents(data)).catch(() => {});
    api.get(`/rooms/${id}/waiting`).then(({ data }) => setWaiting(data)).catch(() => {});
    api.get(`/rooms/${id}/files`).then(({ data }) => setFiles(data)).catch(() => {});
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const int = setInterval(() => {
      api.get(`/rooms/${id}/waiting`).then(({ data }) => setWaiting(data)).catch(() => {});
    }, 8000);
    return () => clearInterval(int);
  }, [id]);

  const saveRoom = async () => {
    try {
      const { data } = await api.put(`/rooms/${id}`, {
        name: room.name, description: room.description, capacity: room.capacity,
        color: room.color, is_active: room.is_active, access_type: room.access_type,
      });
      setRoom(data);
      toast.success(t("manage.saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteRoom = async () => {
    if (!window.confirm(t("manage.delete_confirm"))) return;
    try {
      await api.delete(`/rooms/${id}`);
      toast.success(t("manage.deleted"));
      navigate("/dashboard");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const resolveWaiting = async (wid, action) => {
    try {
      await api.post(`/rooms/${id}/waiting/${wid}`, { action });
      setWaiting(waiting.filter((w) => w.id !== wid));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/rooms/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("manage.uploaded"));
      api.get(`/rooms/${id}/files`).then(({ data }) => setFiles(data)).catch(() => {});
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); if (fileInput.current) fileInput.current.value = ""; }
  };

  const deleteFile = async (fid) => {
    try {
      await api.delete(`/files/${fid}`);
      setFiles(files.filter((f) => f.id !== fid));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addMember = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/rooms/${id}/members`, newMember);
      setNewMember({ email: "", role: "participant" });
      toast.success(t("manage.member_added"));
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const removeMember = async (mid) => {
    try {
      await api.delete(`/rooms/${id}/members/${mid}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addEvent = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/rooms/${id}/events`, {
        ...newEvent,
        start_time: new Date(newEvent.start_time).toISOString(),
        duration_minutes: Number(newEvent.duration_minutes),
      });
      setNewEvent({ title: "", description: "", start_time: "", duration_minutes: 60, recurrence: "none" });
      toast.success(t("manage.event_added"));
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const removeEvent = async (eid) => {
    try {
      await api.delete(`/rooms/${id}/events/${eid}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!room) return <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center text-slate-400">…</div>;

  const tabs = [
    { id: "settings", label: t("manage.tab_settings"), icon: Settings2 },
    { id: "members", label: t("manage.tab_members"), icon: Users },
    { id: "events", label: t("manage.tab_events"), icon: CalendarDays },
    { id: "waiting", label: `${t("manage.tab_waiting")}${waiting.length ? ` (${waiting.length})` : ""}`, icon: Hourglass },
    { id: "files", label: t("manage.tab_files"), icon: FileText },
  ];

  const inputCls = "w-full px-4 py-3 rounded-xl bg-[#0B0F17] border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500";

  return (
    <div className="min-h-screen bg-[#0B0F17]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 pt-28 pb-20" data-testid="room-manage-page">
        <Link to="/dashboard" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" /> {t("wizard.back_overview")}
        </Link>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl" style={{ backgroundColor: room.color }} />
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-white">{room.name}</h1>
              <span className="font-mono text-xs text-indigo-300">digisidlo.cz/{room.full_slug}</span>
            </div>
          </div>
          <Link to={`/r/${room.full_slug}`} data-testid="manage-enter-room"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 transition-colors">
            <ExternalLink className="w-4 h-4" /> {t("manage.enter_room")}
          </Link>
        </div>

        <div className="mt-8 flex gap-1 sm:gap-2 border-b border-slate-800 overflow-x-auto">
          {tabs.map((x) => (
            <button key={x.id} data-testid={`manage-tab-${x.id}`} onClick={() => setTab(x.id)}
              className={`flex items-center gap-2 px-3 sm:px-4 py-3 text-xs sm:text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === x.id ? "border-indigo-500 text-white" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
              <x.icon className="w-4 h-4" /> {x.label}
            </button>
          ))}
        </div>

        {tab === "settings" && (
          <div className="mt-8 bg-[#131B2A] border border-slate-800 rounded-2xl p-8 space-y-5">
            <div>
              <label className="text-sm font-semibold text-slate-300">{t("manage.name")}</label>
              <input data-testid="settings-name-input" value={room.name} onChange={(e) => setRoom({ ...room, name: e.target.value })} className={`mt-2 ${inputCls}`} />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-300">{t("manage.desc")}</label>
              <textarea data-testid="settings-description-input" rows={3} value={room.description} onChange={(e) => setRoom({ ...room, description: e.target.value })} className={`mt-2 ${inputCls} resize-none`} />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-300">{t("manage.access")}</label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {["public", "private"].map((a) => (
                  <button key={a} data-testid={`settings-access-${a}`} onClick={() => setRoom({ ...room, access_type: a })}
                    className={`p-4 rounded-xl border text-left transition-colors ${room.access_type === a ? "bg-indigo-950/50 border-indigo-500" : "bg-[#0B0F17] border-slate-700 hover:border-indigo-500"}`}>
                    <div className="font-semibold text-white text-sm">{t(a === "public" ? "wizard.public_title" : "wizard.private_title")}</div>
                    <div className="mt-1 text-xs text-slate-500">{t(a === "public" ? "wizard.public_desc" : "wizard.private_desc")}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-300">{t("manage.capacity")}</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {CAPACITIES.map((c) => (
                  <button key={c} onClick={() => setRoom({ ...room, capacity: c })}
                    className={`px-4 py-2 rounded-xl font-mono text-sm border transition-colors ${room.capacity === c ? "bg-indigo-600 border-indigo-500 text-white" : "bg-[#0B0F17] border-slate-700 text-slate-400 hover:border-indigo-500"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-300">{t("manage.color")}</label>
              <div className="mt-2 flex gap-3 items-center">
                <input type="color" data-testid="settings-color-input" value={room.color} onChange={(e) => setRoom({ ...room, color: e.target.value })}
                  className="w-12 h-12 rounded-xl bg-transparent border border-slate-700 cursor-pointer" />
                <span className="font-mono text-sm text-slate-400">{room.color}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="active" data-testid="settings-active-checkbox" checked={room.is_active} onChange={(e) => setRoom({ ...room, is_active: e.target.checked })} className="w-4 h-4 accent-indigo-600" />
              <label htmlFor="active" className="text-sm text-slate-300">{t("manage.is_active")}</label>
            </div>
            <div className="pt-4 border-t border-slate-800 flex items-center justify-between">
              <button data-testid="settings-save-button" onClick={saveRoom}
                className="px-6 py-2.5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 transition-colors">
                {t("manage.save")}
              </button>
              <button data-testid="settings-delete-button" onClick={deleteRoom}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-red-900 text-red-400 text-sm font-semibold hover:bg-red-950/50 transition-colors">
                <Trash2 className="w-4 h-4" /> {t("manage.delete")}
              </button>
            </div>
          </div>
        )}

        {tab === "members" && (
          <div className="mt-8 space-y-6">
            <form onSubmit={addMember} className="bg-[#131B2A] border border-slate-800 rounded-2xl p-6 flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[220px]">
                <label className="text-sm font-semibold text-slate-300">{t("manage.member_email")}</label>
                <input data-testid="member-email-input" required type="email" value={newMember.email}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                  placeholder="clen@example.com" className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-300">{t("manage.member_role")}</label>
                <select data-testid="member-role-select" value={newMember.role}
                  onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                  className="mt-2 px-4 py-3 rounded-xl bg-[#0B0F17] border border-slate-700 text-white focus:outline-none focus:border-indigo-500">
                  <option value="participant">{t("manage.role_participant")}</option>
                  <option value="moderator">{t("manage.role_moderator")}</option>
                  <option value="organizer">{t("manage.role_organizer")}</option>
                </select>
              </div>
              <button data-testid="member-add-button" className="flex items-center gap-2 px-5 py-3 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 transition-colors">
                <Plus className="w-4 h-4" /> {t("manage.member_add")}
              </button>
            </form>
            <div className="space-y-2">
              {members.length === 0 && <p className="text-sm text-slate-500">{t("manage.no_members")}</p>}
              {members.map((m) => (
                <div key={m.id} data-testid={`member-row-${m.email}`} className="flex items-center gap-4 bg-[#131B2A] border border-slate-800 rounded-xl px-5 py-3.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{m.name || m.email}</div>
                    <div className="text-xs text-slate-500 truncate">{m.email}</div>
                  </div>
                  <span className="text-xs font-semibold px-3 py-1 rounded-full bg-indigo-950/80 text-indigo-300 border border-indigo-700/50">{ROLE_LABEL[m.role]}</span>
                  <button data-testid={`member-remove-${m.email}`} onClick={() => removeMember(m.id)} className="text-slate-500 hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "events" && (
          <div className="mt-8 space-y-6">
            <form onSubmit={addEvent} className="bg-[#131B2A] border border-slate-800 rounded-2xl p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-300">{t("manage.event_title")}</label>
                  <input data-testid="event-title-input" required value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    placeholder="Meeting" className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-300">{t("manage.event_start")}</label>
                  <input data-testid="event-start-input" required type="datetime-local" value={newEvent.start_time}
                    onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })} className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-300">{t("manage.event_duration")}</label>
                  <input data-testid="event-duration-input" type="number" min="15" step="15" value={newEvent.duration_minutes}
                    onChange={(e) => setNewEvent({ ...newEvent, duration_minutes: e.target.value })} className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-300">{t("manage.event_recurrence")}</label>
                  <select data-testid="event-recurrence-select" value={newEvent.recurrence}
                    onChange={(e) => setNewEvent({ ...newEvent, recurrence: e.target.value })}
                    className={`mt-2 ${inputCls}`}>
                    <option value="none">{t("manage.rec_none")}</option>
                    <option value="daily">{t("manage.rec_daily")}</option>
                    <option value="weekly">{t("manage.rec_weekly")}</option>
                    <option value="monthly">{t("manage.rec_monthly")}</option>
                  </select>
                </div>
              </div>
              <button data-testid="event-add-button" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 transition-colors">
                <Plus className="w-4 h-4" /> {t("manage.event_add")}
              </button>
            </form>
            <div className="space-y-2">
              {events.length === 0 && <p className="text-sm text-slate-500">{t("manage.no_events")}</p>}
              {events.map((e) => (
                <div key={e.id} data-testid={`event-row-${e.id}`} className="flex items-center gap-4 bg-[#131B2A] border border-slate-800 rounded-xl px-5 py-3.5">
                  <CalendarDays className="w-5 h-5 text-amber-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{e.title}</div>
                    <div className="text-xs text-slate-500">
                      {new Date(e.start_time).toLocaleString("cs-CZ")} · {e.duration_minutes} min · {REC_LABEL[e.recurrence]}
                    </div>
                  </div>
                  <button data-testid={`event-remove-${e.id}`} onClick={() => removeEvent(e.id)} className="text-slate-500 hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "waiting" && (
          <div className="mt-8 space-y-2" data-testid="waiting-list">
            {waiting.length === 0 && <p className="text-sm text-slate-500">{t("manage.waiting_empty")}</p>}
            {waiting.map((w) => (
              <div key={w.id} data-testid={`waiting-row-${w.id}`} className="flex items-center gap-4 bg-[#131B2A] border border-slate-800 rounded-xl px-5 py-3.5">
                <Hourglass className="w-4 h-4 text-amber-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{w.name}</div>
                  <div className="text-xs text-slate-500">{new Date(w.created_at).toLocaleTimeString("cs-CZ")}</div>
                </div>
                <button data-testid={`waiting-approve-${w.id}`} onClick={() => resolveWaiting(w.id, "approve")}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-500 transition-colors">
                  <Check className="w-3.5 h-3.5" /> {t("manage.approve")}
                </button>
                <button data-testid={`waiting-deny-${w.id}`} onClick={() => resolveWaiting(w.id, "deny")}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-red-900 text-red-400 text-xs font-semibold hover:bg-red-950/50 transition-colors">
                  <X className="w-3.5 h-3.5" /> {t("manage.deny")}
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "files" && (
          <div className="mt-8 space-y-4">
            <div>
              <input ref={fileInput} type="file" className="hidden" onChange={uploadFile}
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.mp4,.mp3" data-testid="file-upload-input" />
              <button data-testid="file-upload-button" onClick={() => fileInput.current?.click()} disabled={uploading}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 transition-colors disabled:opacity-50">
                <Upload className="w-4 h-4" /> {uploading ? t("manage.uploading") : t("manage.upload")}
              </button>
            </div>
            <div className="space-y-2" data-testid="manage-files-list">
              {files.length === 0 && <p className="text-sm text-slate-500">{t("manage.files_empty")}</p>}
              {files.map((f) => (
                <div key={f.id} className="flex items-center gap-4 bg-[#131B2A] border border-slate-800 rounded-xl px-5 py-3.5">
                  <FileText className="w-4 h-4 text-indigo-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{f.original_filename}</div>
                    <div className="text-xs text-slate-500">{fmtSize(f.size)} · {f.uploader_name}</div>
                  </div>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/download`} data-testid={`manage-file-download-${f.id}`}
                    className="p-2 rounded-full border border-slate-700 text-slate-400 hover:text-white hover:border-indigo-500 transition-colors">
                    <Download className="w-3.5 h-3.5" />
                  </a>
                  <button data-testid={`manage-file-delete-${f.id}`} onClick={() => deleteFile(f.id)} className="text-slate-500 hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
